import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlugins } from '@/contexts/PluginContext';
import { getArcadeSounds } from '@/shared/utils/ArcadeSounds';
import {
  BookmarkCheck,
  CalendarDays,
  Files,
  PlusCircle,
} from 'lucide-react';

/**
 * BottomNavVideogame Component
 * Mobile bottom navigation bar with Chamama arcade styling
 *
 * Design:
 * - Fixed to bottom of screen
 * - WCAG 2.2 compliant touch targets (min 44x44px)
 * - Chamama color palette with thick black borders
 * - Only visible on mobile (<700px)
 */
export default function BottomNavVideogame() {
  const location = useLocation();
  const { t } = useTranslation();
  const { isPluginEnabled } = usePlugins();

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Haptic feedback utility
  const triggerHapticFeedback = (pattern: number | number[] = 20) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  // Build navigation items (same as sidebar)
  const navItems: Array<{
    path: string;
    label: string;
    icon: React.ComponentType<{ className?: string; size?: number }>;
  }> = [];

  if (isPluginEnabled('BOOKING')) {
    navItems.push(
      { path: '/bookings/my-bookings', label: t('navigation.myBookings'), icon: BookmarkCheck },
      { path: '/calendar', label: t('navigation.calendar'), icon: CalendarDays },
      { path: '/bookings/my-bookings?book=true', label: t('navigation.bookNow'), icon: PlusCircle },
      // { path: '/bookings/rooms', label: t('navigation.bookRoom'), icon: DoorOpen },
      // { path: '/bookings/equipment', label: t('navigation.bookEquipment'), icon: Package },
    );
  }

  // Files nav item (always visible, not plugin-gated)
  navItems.push({ path: '/files', label: t('navigation.files'), icon: Files });

  // If no navigation items, don't render
  if (navItems.length === 0) return null;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{
        backgroundColor: '#F4D35E',
        borderTop: '4px solid #000000',
        paddingBottom: 'env(safe-area-inset-bottom)', // iOS safe area support
      }}
    >
      <div className="flex justify-around items-center px-2 py-2" dir="ltr">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center justify-center rounded-full transition-all duration-200"
              style={{
                backgroundColor: active ? '#FFB6F3' : 'transparent', // Pink when active
                border: active ? '3px solid #000000' : '3px solid transparent',
                boxShadow: active ? '2px 2px 0px #000000' : 'none',
                width: '56px',
                height: '56px', // WCAG 2.2: 44px minimum + padding
              }}
              onClick={() => {
                triggerHapticFeedback();
                getArcadeSounds()?.playClick();
              }}
              aria-current={active ? 'page' : undefined}
              aria-label={item.label}
            >
              <Icon
                size={28}
                className="h-7 w-7 text-black"
              />
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
